import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AppTopBar from '../components/layout/AppTopBar';
import WorkspaceCard from '../components/workspace/WorkspaceCard';
import QuickUploadModal from '../components/workspace/QuickUploadModal';
import LimitReachedModal from '../components/workspace/LimitReachedModal';
import Btn from '../components/ui/Btn';
import { Card, Badge, Spinner } from '../components/ui/primitives';
import { Icon } from '../components/ui/Icon';
import { ErrorBanner } from './auth/SignInScreen';
import { supabase } from '../lib/supabase';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../lib/toast';
import { getUserPlan } from '../lib/plan';
import { useIsMobile } from '../lib/useBreakpoint';

// Stable colors per team — derived from team id so the same team always
// gets the same color across reloads
const TEAM_COLORS = ['#0a0f2e', '#3dc75b', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#ef4444'];
function colorForTeamId(id) {
  const hash = String(id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return TEAM_COLORS[hash % TEAM_COLORS.length];
}

// Friendly date for the hero band
function formatHeroDate(d = new Date()) {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).toUpperCase();
}

// Friendly time-of-day greeting
function timeGreeting(d = new Date()) {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

// Friendly relative time, e.g. "2h ago"
function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)        return 'just now';
  if (diff < 3600)      return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400)     return Math.floor(diff / 3600) + 'h ago';
  if (diff < 86400 * 7) return Math.floor(diff / 86400) + 'd ago';
  return new Date(iso).toLocaleDateString();
}

export default function HubScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const confirm = useConfirm();
  const toast = useToast();

  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [teams, setTeams]           = useState([]);    // [{ team, role, isOwner, ... }]
  const [invites, setInvites]       = useState([]);    // pending team_invites
  const [counts, setCounts]         = useState({});    // { [teamId]: { pending, rejected, missing, usage } }
  const [userPlan, setUserPlan]     = useState(null);  // { plan, limits } for current user — Phase 2
  const [actingInviteId, setActingInviteId] = useState(null);
  const [quickUploadOpen, setQuickUploadOpen] = useState(false);

  // Phase 3A: workspace limit modal
  const [limitHit, setLimitHit] = useState(null);

  // Phase B: an invite the user arrived through via ?invite=<id> (existing-user
  // email link). Captured once so it survives the post-accept URL cleanup below.
  const [inviteParam] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('invite'); }
    catch { return null; }
  });

  // Load everything in one effect
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // 0) Phase B: if we arrived via ?invite=<id>, accept THAT invite first
        //    (consent-scoped — never a sweep) through the service-role
        //    /api/accept-invite route, then strip the param so a refresh doesn't
        //    re-run it. Soft-fail: the pending-invite card remains as fallback.
        if (inviteParam) {
          try {
            await api.acceptInvite({ invite_id: inviteParam });
          } catch (e) {
            console.warn('Hub auto-accept failed (invite card remains as fallback):', e.message);
          } finally {
            const url = new URL(window.location.href);
            url.searchParams.delete('invite');
            window.history.replaceState({}, '', url.pathname + url.search + url.hash);
          }
        }

        // 1) Load all team_members rows for this user, joined with their team + role
        const { data: memberships, error: mErr } = await supabase
          .from('team_members')
          .select(`
            id, team_id, role_id, is_owner, email, first_name, last_name,
            teams ( id, name, slug, plan, uploads_used, uploads_limit, primary_color, logo_url ),
            team_roles ( id, name, slug, is_staff )
          `)
          .eq('user_id', user.id);
        if (mErr) throw mErr;
        if (cancelled) return;

        const validMemberships = (memberships || []).filter(m => m.teams);
        const teamRows = validMemberships.map(m => ({
          membershipId: m.id,
          team: m.teams,
          role: m.team_roles,
          isOwner: m.is_owner,
        }));
        setTeams(teamRows);

        // 2) Load pending invites for this user's email
        const { data: inviteRows, error: iErr } = await supabase
          .from('team_invites')
          .select(`
            id, team_id, email, role_id, status, created_at,
            teams ( id, name ),
            team_roles ( id, name )
          `)
          .eq('email', (user.email || '').toLowerCase())
          .eq('status', 'pending')
          .order('created_at', { ascending: false });
        if (!iErr) setInvites(inviteRows || []);

        // 3) Load notification counts per team (best-effort, non-blocking)
        const teamIds = teamRows.map(r => r.team.id);
        if (teamIds.length > 0) {
          await loadCountsForTeams(teamIds, validMemberships, setCounts);
        }

        // 4) Load user's plan from profiles (Phase 2 — user-level plan)
        const planInfo = await getUserPlan(user.id);
        if (!cancelled) setUserPlan(planInfo);
      } catch (err) {
        console.error('Hub load failed:', err);
        if (!cancelled) setError(err.message || 'Failed to load your workspaces.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // ────────────────────────────────────────────────────────────────────────
  // Phase 3A: workspace limit check before navigating to /create-team
  // Only the owner's workspace count matters (teams joined as a member
  // don't count toward the creator's workspace quota).
  // ────────────────────────────────────────────────────────────────────────
  const handleCreateWorkspace = () => {
    if (!userPlan) {
      // Plan hasn't loaded yet — let them proceed, backend will gate
      navigate('/create-team');
      return;
    }
    const ownedCount = teams.filter(t => t.isOwner).length;
    if (ownedCount >= userPlan.limits.workspaces) {
      setLimitHit({
        type: 'workspaces',
        used: ownedCount,
        limit: userPlan.limits.workspaces,
        plan: userPlan.plan,
      });
      return;
    }
    navigate('/create-team');
  };

  // ────────────────────────────────────────────────────────────────────────
  // Invite accept / decline
  // ────────────────────────────────────────────────────────────────────────
  const acceptInvite = async (inv) => {
    if (actingInviteId) return;
    setActingInviteId(inv.id);
    try {
      // Accept server-side (service-role): creates the membership + marks the
      // invite accepted in one call. Keeps all invite acceptance off the client
      // so the self-insert RLS policy can be locked to owners only (Phase C).
      await api.acceptInvite({ invite_id: inv.id });

      // Optimistically update local state
      setInvites(invites.filter(i => i.id !== inv.id));
      // Reload teams so the new one shows up
      window.location.reload();
    } catch (err) {
      console.error('Accept invite failed:', err);
      toast.error('Could not accept invite: ' + (err.message || 'unknown error'));
      setActingInviteId(null);
    }
  };

  const declineInvite = async (inv) => {
    if (actingInviteId) return;
    const r = await confirm({
      title: 'Decline invite',
      message: `Decline the invite to ${inv.teams?.name || 'this workspace'}?`,
      confirmLabel: 'Decline',
    });
    if (!r.confirmed) return;
    setActingInviteId(inv.id);
    try {
      const { error: updErr } = await supabase
        .from('team_invites')
        .update({ status: 'declined' })
        .eq('id', inv.id);
      if (updErr) throw updErr;
      setInvites(invites.filter(i => i.id !== inv.id));
    } catch (err) {
      console.error('Decline invite failed:', err);
      toast.error('Could not decline invite: ' + (err.message || 'unknown error'));
    } finally {
      setActingInviteId(null);
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  // Derived hero stats
  // ────────────────────────────────────────────────────────────────────────
  const heroStats = useMemo(() => {
    const totalPending = Object.values(counts).reduce((s, c) => s + (c.pending  || 0), 0);
    const totalUsage   = Object.values(counts).reduce((s, c) => s + (c.usage    || 0), 0);
    const totalRej     = Object.values(counts).reduce((s, c) => s + (c.rejected || 0), 0);
    const totalMissing = Object.values(counts).reduce((s, c) => s + (c.missing  || 0), 0);
    return { totalPending, totalUsage, totalRej, totalMissing };
  }, [counts]);

  const totalNotifications = heroStats.totalPending + heroStats.totalRej + invites.length;

  // ────────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <AppTopBar notificationCount={totalNotifications} />

      {/* Hero band */}
      <div style={{
        background: 'linear-gradient(135deg, #0a0f2e 0%, #131a3d 55%, #1e2654 100%)',
        color: '#fff',
        padding: isMobile ? '24px 16px 28px' : '36px 32px 36px',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -60, right: -60, width: 400, height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(61,199,91,0.25) 0%, transparent 60%)',
          pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
          <div style={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 32,
            flexWrap: 'wrap',
          }}>
            <div>
              <div style={{
                fontSize: 12, color: '#8be89e', fontWeight: 600,
                letterSpacing: '0.08em', marginBottom: 6,
              }}>
                {formatHeroDate()}
              </div>
              <h1 style={{
                fontSize: isMobile ? 26 : 34, fontWeight: 700, letterSpacing: '-0.03em',
                lineHeight: 1.1, marginBottom: 10,
              }}>
                {timeGreeting()}{user?.user_metadata?.first_name ? `, ${user.user_metadata.first_name}` : ''}.
              </h1>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.72)', maxWidth: 540 }}>
                {heroSubtitle(heroStats, teams.length, invites.length)}
              </p>
            </div>

            <div style={{
              display: 'flex', gap: 10,
              width: isMobile ? '100%' : undefined,
              flexDirection: isMobile ? 'column' : 'row',
            }}>
              <Btn
                variant="outline"
                size="lg"
                icon={Icon.upload}
                onClick={() => setQuickUploadOpen(true)}
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.2)',
                  width: isMobile ? '100%' : undefined,
                  justifyContent: isMobile ? 'center' : undefined,
                }}
              >
                Quick Upload
              </Btn>
              <Btn
                variant="primary"
                size="lg"
                icon={Icon.plus}
                onClick={handleCreateWorkspace}
                style={{
                  width: isMobile ? '100%' : undefined,
                  justifyContent: isMobile ? 'center' : undefined,
                }}
              >
                New workspace
              </Btn>
            </div>
          </div>

          {/* Stat strip */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? 'repeat(2, 1fr)'
              : 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: isMobile ? 8 : 12,
            marginTop: isMobile ? 24 : 32,
          }}>
            <HeroStat
              label="Workspaces"
              value={teams.length}
              icon={Icon.folder}
            />
            <HeroStat
              label="Pending review"
              value={heroStats.totalPending}
              alert={heroStats.totalPending > 0}
              icon={Icon.clock}
            />
            <HeroStat
              label="Docs this month"
              value={heroStats.totalUsage}
              icon={Icon.docs}
            />
            <HeroStat
              label="Pending invites"
              value={invites.length}
              accent={invites.length > 0}
              icon={Icon.inbox}
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        padding: isMobile ? '20px 16px 48px' : '24px 32px 60px',
        width: '100%',
        position: 'relative', zIndex: 2,
        // Hard containment: no child can visually overflow this wrapper.
        // Guards against any future card content that might escape its column.
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}>
        {error && <ErrorBanner message={error} style={{ marginBottom: 20 }} />}

        {/* Pending invites — render each as its own banner */}
        {invites.map((inv) => (
          <Card
            key={inv.id}
            style={{
              padding: isMobile ? '14px' : '14px 18px', marginBottom: 12,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: isMobile ? 12 : 14,
              boxShadow: 'var(--shadow-md)',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              flex: isMobile ? undefined : 1, minWidth: 0,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'var(--accent-bg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Icon.inbox size={16} stroke="var(--accent-600)" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                  You're invited to <strong>{inv.teams?.name || 'a workspace'}</strong>
                  {inv.team_roles?.name && (
                    <> as <strong>{inv.team_roles.name}</strong></>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                  Invited {timeAgo(inv.created_at)}
                </div>
              </div>
            </div>
            <div style={{
              display: 'flex', gap: 8,
              flexShrink: 0,
              width: isMobile ? '100%' : undefined,
            }}>
              <Btn
                variant="outline"
                size="sm"
                onClick={() => declineInvite(inv)}
                disabled={actingInviteId === inv.id}
                style={{
                  flex: isMobile ? 1 : undefined,
                  justifyContent: isMobile ? 'center' : undefined,
                }}
              >
                Decline
              </Btn>
              <Btn
                variant="primary"
                size="sm"
                icon={actingInviteId === inv.id ? null : Icon.check}
                onClick={() => acceptInvite(inv)}
                disabled={actingInviteId === inv.id}
                style={{
                  flex: isMobile ? 2 : undefined,
                  justifyContent: isMobile ? 'center' : undefined,
                }}
              >
                {actingInviteId === inv.id ? <><Spinner size={12} color="rgba(255,255,255,0.6)" /> Accepting…</> : 'Accept invite'}
              </Btn>
            </div>
          </Card>
        ))}

        {/* Split teams into owned vs joined */}
        {(() => {
          const ownedTeams = teams.filter(t => t.isOwner);
          const joinedTeams = teams.filter(t => !t.isOwner);

          // Compute combined usage across all owned teams (for the stacked bar)
          // Phase 2: plan comes from profiles (userPlan), not teams
          const ownerPlan = (userPlan?.plan || 'free').toLowerCase();
          const ownerLimit = userPlan?.limits?.ai ?? 30;
          const ownedUsageTotal = ownedTeams.reduce((s, t) => s + (t.team.uploads_used || 0), 0);
          const showUsageBar = ownedTeams.length > 0 && ownerPlan !== 'business' && userPlan !== null;

          const renderCard = ({ team, role, isOwner }) => {
            const c = counts[team.id] || {};
            const isClient = role && !role.is_staff && (role.slug || '').toLowerCase() === 'client';
            return (
              <WorkspaceCard
                key={team.id}
                w={{
                  id: team.id,
                  name: team.name,
                  slug: team.slug,
                  color: team.primary_color || colorForTeamId(team.id),
                  plan: team.plan || 'free',
                  roleSlug: role?.slug,
                  roleName: isOwner ? 'Owner' : (role?.name || 'Member'),
                  isClient,
                  members: c.members,
                  usage: team.uploads_used != null ? team.uploads_used : c.usage,
                  limit: team.uploads_limit,
                  trend: c.trend,
                  pending: c.pending || 0,
                  rejected: c.rejected || 0,
                  missing: c.missing || 0,
                }}
              />
            );
          };

          // Loading state
          if (loading && teams.length === 0) {
            return (
              <Card style={{ padding: 48, textAlign: 'center' }}>
                <Spinner size={20} color="var(--accent)" />
                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-3)' }}>Loading your workspaces…</div>
              </Card>
            );
          }

          return (
            <>
              {/* OWNED WORKSPACES */}
              {ownedTeams.length > 0 && (
                <div style={{ marginBottom: joinedTeams.length > 0 ? 40 : 0 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: 16, paddingTop: 8,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <h2 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text)' }}>
                        Your workspaces
                      </h2>
                      <Badge variant="neutral">{ownedTeams.length}</Badge>
                    </div>
                    {ownerPlan !== 'business' && (
                      <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                        Billed to you · <strong style={{ color: 'var(--text-2)', textTransform: 'capitalize' }}>{ownerPlan}</strong> plan
                      </div>
                    )}
                  </div>

                  {/* Stacked usage bars (AI + Storage) across all owned teams */}
                  {showUsageBar && (
                    <UsageMeters
                      teams={ownedTeams}
                      counts={counts}
                      plan={ownerPlan}
                      limits={userPlan.limits}
                      onUpgrade={() => navigate('/account?tab=billing')}
                    />
                  )}

                  {/* Business plan — just show a subtle unlimited badge */}
                  {ownedTeams.length > 0 && ownerPlan === 'business' && (
                    <div style={{
                      marginBottom: 16, padding: '10px 14px',
                      background: 'var(--accent-bg)',
                      borderRadius: 'var(--r-md)',
                      display: 'flex', alignItems: 'center', gap: 10,
                      fontSize: 12.5, color: 'var(--text-2)',
                    }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: 6,
                        background: 'var(--accent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Icon.zap size={11} stroke="#fff" strokeWidth={2.5} />
                      </div>
                      <span>
                        <strong style={{ color: 'var(--accent-600)', fontWeight: 700 }}>{ownedUsageTotal.toLocaleString()}</strong> AI classifications used — unlimited on Business plan
                      </span>
                    </div>
                  )}

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                      ? '1fr'
                      : 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: isMobile ? 12 : 16,
                    minWidth: 0, maxWidth: '100%',
                  }}>
                    {ownedTeams.map(renderCard)}
                    <CreateTile onClick={handleCreateWorkspace} />
                  </div>
                </div>
              )}

              {/* JOINED WORKSPACES */}
              {joinedTeams.length > 0 && (
                <div>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: 16, paddingTop: 8,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <h2 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text)' }}>
                        {ownedTeams.length > 0 ? 'Joined workspaces' : 'Your workspaces'}
                      </h2>
                      <Badge variant="neutral">{joinedTeams.length}</Badge>
                    </div>
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                      ? '1fr'
                      : 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: isMobile ? 12 : 16,
                    minWidth: 0, maxWidth: '100%',
                  }}>
                    {joinedTeams.map(renderCard)}
                    {/* If they don't own any teams, show the CreateTile here instead */}
                    {ownedTeams.length === 0 && <CreateTile onClick={handleCreateWorkspace} />}
                  </div>
                </div>
              )}

              {/* EMPTY: no owned AND no joined */}
              {ownedTeams.length === 0 && joinedTeams.length === 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile
                    ? '1fr'
                    : 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: isMobile ? 12 : 16,
                  minWidth: 0, maxWidth: '100%',
                }}>
                  <CreateTile onClick={handleCreateWorkspace} />
                </div>
              )}
            </>
          );
        })()}
      </div>

      {quickUploadOpen && (
        <QuickUploadModal
          teams={teams.map(({ team, role, isOwner }) => ({
            id: team.id,
            name: team.name,
            primary_color: team.primary_color,
            isOwner,
            roleName: role?.name || (isOwner ? 'Owner' : 'Member'),
          }))}
          onClose={() => setQuickUploadOpen(false)}
          onUploaded={(_doc, teamId) => {
            // Bump pending count for that team locally so the card reflects it
            setCounts(prev => ({
              ...prev,
              [teamId]: {
                ...(prev[teamId] || {}),
                pending: ((prev[teamId]?.pending) || 0) + 1,
              },
            }));
          }}
        />
      )}

      {limitHit && (
        <LimitReachedModal
          type={limitHit.type}
          used={limitHit.used}
          limit={limitHit.limit}
          plan={limitHit.plan}
          onClose={() => setLimitHit(null)}
          onUpgrade={() => {
            setLimitHit(null);
            navigate('/account?tab=billing');
          }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function heroSubtitle({ totalPending, totalRej, totalMissing }, teamCount, inviteCount) {
  const parts = [];
  if (totalPending > 0) parts.push(`${totalPending} document${totalPending === 1 ? '' : 's'} need${totalPending === 1 ? 's' : ''} review`);
  if (totalRej > 0)     parts.push(`${totalRej} rejection${totalRej === 1 ? '' : 's'} to follow up`);
  if (totalMissing > 0) parts.push(`${totalMissing} doc${totalMissing === 1 ? '' : 's'} still pending your upload`);
  if (inviteCount > 0)  parts.push(`${inviteCount} pending invite${inviteCount === 1 ? '' : 's'}`);

  if (parts.length === 0) {
    if (teamCount === 0) return 'Welcome! Create your first workspace to start collecting documents.';
    return 'You\'re all caught up across your workspaces.';
  }
  return parts.join(' · ') + '.';
}

function HeroStat({ label, value, alert, accent, icon: I }) {
  let valueColor = '#8be89e';
  if (alert)  valueColor = '#fbbf24';
  if (accent && value > 0) valueColor = '#8be89e';

  return (
    <div style={{
      padding: '16px 18px',
      background: 'rgba(255,255,255,0.06)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 14,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 6,
      }}>
        <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>{label}</span>
        {I && <I size={14} stroke="rgba(255,255,255,0.5)" />}
      </div>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8,
        fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em',
        color: value > 0 ? valueColor : '#ffffff',
      }}>
        {value}
      </div>
    </div>
  );
}

function CreateTile({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: '2px dashed var(--border-strong)',
        borderRadius: 14, padding: 24,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', cursor: 'pointer',
        minHeight: 260, color: 'var(--text-3)',
        transition: 'all 0.15s', background: 'transparent',
        fontFamily: 'inherit',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--accent)';
        e.currentTarget.style.background = 'var(--accent-bg)';
        e.currentTarget.style.color = 'var(--accent-600)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border-strong)';
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = 'var(--text-3)';
      }}
    >
      <div style={{
        width: 48, height: 48, borderRadius: 14,
        background: 'var(--surface)', border: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 12,
      }}>
        <Icon.plus size={20} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
        Create new workspace
      </div>
      <div style={{ fontSize: 12 }}>Fresh space for a new team or client</div>
    </button>
  );
}

// Loads notification counts in parallel for all teams.
// Uses head:true + count:'exact' for efficient COUNT queries (no row payload).
async function loadCountsForTeams(teamIds, memberships, setCounts) {
  // Build a quick lookup: teamId -> { membershipId, isStaff, isClient }
  const memMap = {};
  memberships.forEach(m => {
    memMap[m.team_id] = {
      membershipId: m.id,
      isStaff: !!m.team_roles?.is_staff,
      isClient: !m.team_roles?.is_staff && (m.team_roles?.slug || '').toLowerCase() === 'client',
    };
  });

  const results = await Promise.all(teamIds.map(async (teamId) => {
    const m = memMap[teamId];
    const out = { teamId, members: 0, pending: 0, rejected: 0, missing: 0, storage: 0 };

    try {
      // member count (everyone can count; RLS may scope it)
      const { count: memberCount } = await supabase
        .from('team_members')
        .select('id', { count: 'exact', head: true })
        .eq('team_id', teamId);
      if (memberCount != null) out.members = memberCount;

      // Total documents stored in this team (for storage usage bar)
      const { count: storageCount } = await supabase
        .from('document_uploads')
        .select('id', { count: 'exact', head: true })
        .eq('team_id', teamId);
      if (storageCount != null) out.storage = storageCount;

      // For staff: count uploads with status=in_review for this team
      if (m?.isStaff) {
        const { count: pendingCount } = await supabase
          .from('document_uploads')
          .select('id', { count: 'exact', head: true })
          .eq('team_id', teamId)
          .eq('status', 'in_review');
        if (pendingCount != null) out.pending = pendingCount;
      }

      // For client: count their own rejected uploads (need to fix up later)
      if (m?.isClient && m.membershipId) {
        const { count: rejectedCount } = await supabase
          .from('document_uploads')
          .select('id', { count: 'exact', head: true })
          .eq('team_id', teamId)
          .eq('member_id', m.membershipId)
          .eq('status', 'rejected');
        if (rejectedCount != null) out.rejected = rejectedCount;
      }
    } catch (err) {
      console.warn('Counts failed for team ' + teamId, err);
    }
    return out;
  }));

  const byId = {};
  results.forEach(r => { byId[r.teamId] = r; });
  setCounts(byId);
}

// ────────────────────────────────────────────────────────────────────────────
// UsageMeters — shows AI + Storage meters side-by-side to keep workspace
// cards visible above the fold.
// ────────────────────────────────────────────────────────────────────────────
function UsageMeters({ teams, counts, plan, limits, onUpgrade }) {
  const aiTotal = teams.reduce((s, t) => s + (t.team.uploads_used || 0), 0);
  const storageTotal = teams.reduce((s, t) => s + (counts[t.team.id]?.storage || 0), 0);

  // Treat any limit at or above 100,000 as "unlimited" — reflects the
  // 999,999 sentinel used in the DB and any future Business plan setups.
  const aiLimit = limits.ai >= 100000 ? Infinity : limits.ai;
  const storageLimit = limits.storage >= 100000 ? Infinity : limits.storage;

  return (
    <div style={{
      marginBottom: 20, padding: '14px 16px',
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--r-md)',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: 24,
    }}>
      {/* AI classifications meter */}
      <MeterRow
        label="AI classifications"
        teams={teams}
        valueOf={(t) => t.team.uploads_used || 0}
        total={aiTotal}
        limit={aiLimit}
        onUpgrade={onUpgrade}
        unitLabel="classifications"
        overLimitMessage="AI paused. Upgrade or wait for cycle reset."
      />

      {/* Storage meter */}
      <MeterRow
        label="Documents stored"
        teams={teams}
        valueOf={(t) => counts[t.team.id]?.storage || 0}
        total={storageTotal}
        limit={storageLimit}
        onUpgrade={onUpgrade}
        unitLabel="docs"
        overLimitMessage="Storage full. Delete or upgrade."
      />
    </div>
  );
}

// One row of the meter — used twice (AI + Storage). Handles a finite numeric
// limit OR Infinity (unlimited).
function MeterRow({ label, teams, valueOf, total, limit, onUpgrade, unitLabel, overLimitMessage }) {
  const isUnlimited = !isFinite(limit);
  const pct = isUnlimited ? 0 : (limit > 0 ? Math.min(100, Math.round((total / limit) * 100)) : 0);
  const tier = isUnlimited ? 'ok' : (pct >= 100 ? 'over' : pct >= 80 ? 'warn' : 'ok');

  const tierColor = {
    ok:   'var(--accent)',
    warn: 'var(--warn)',
    over: 'var(--danger)',
  }[tier];

  // For each team, compute its segment width as % of limit (only meaningful
  // when there's an actual limit — for unlimited we show a single solid bar).
  const segments = teams
    .map((t) => {
      const used = valueOf(t);
      const segPct = (!isUnlimited && limit > 0) ? (used / limit) * 100 : 0;
      return {
        id: t.team.id,
        name: t.team.name,
        color: t.team.primary_color || colorForTeamId(t.team.id),
        used,
        pct: segPct,
      };
    })
    .filter(s => isUnlimited ? s.used > 0 : s.pct > 0);

  const showOverflow = !isUnlimited && pct >= 100;

  return (
    <div style={{ minWidth: 0 }}>
      {/* Top row: label + total */}
      <div style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        marginBottom: 8, gap: 8,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 2 }}>
            {label}
          </div>
          <div style={{
            fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em',
            color: tier === 'ok' ? 'var(--text)' : tierColor,
          }}>
            {total.toLocaleString()}{' '}
            <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 500 }}>
              {isUnlimited ? '· Unlimited' : `of ${limit.toLocaleString()}`}
            </span>
          </div>
        </div>
        {!isUnlimited && (
          <div style={{ fontSize: 11.5, color: tier === 'ok' ? 'var(--text-3)' : tierColor, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {pct}% used
          </div>
        )}
      </div>

      {/* Bar — segmented per-team for finite limits, single solid for unlimited */}
      <div style={{
        height: 8, borderRadius: 4,
        background: 'var(--surface-2)',
        overflow: 'hidden',
        display: 'flex',
        border: showOverflow ? '1px solid var(--danger)' : 'none',
      }}>
        {isUnlimited ? (
          // Single subtle bar showing usage exists (unlimited so no fill %)
          total > 0 && (
            <div
              title={`${total.toLocaleString()} ${unitLabel} (unlimited)`}
              style={{
                width: '100%',
                background: 'var(--accent)',
                opacity: 0.35,
                transition: 'width 0.4s ease',
              }}
            />
          )
        ) : (
          segments.map((s, i) => (
            <div
              key={s.id}
              title={`${s.name}: ${s.used.toLocaleString()} ${unitLabel}`}
              style={{
                width: `${Math.min(100, s.pct)}%`,
                background: s.color,
                borderRight: i < segments.length - 1 ? '1px solid rgba(255,255,255,0.8)' : 'none',
                transition: 'width 0.4s ease',
              }}
            />
          ))
        )}
      </div>

      {/* Per-team legend (only when finite limit + multiple teams) */}
      {!isUnlimited && segments.length > 1 && (
        <div style={{
          marginTop: 8,
          display: 'flex', flexWrap: 'wrap', gap: 8,
        }}>
          {segments.map(s => (
            <div key={s.id} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              fontSize: 10.5, color: 'var(--text-2)',
              minWidth: 0,
            }}>
              <div style={{
                width: 6, height: 6, borderRadius: 2,
                background: s.color,
                flexShrink: 0,
              }} />
              <span style={{
                fontWeight: 600, color: 'var(--text)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                maxWidth: 140,
              }}>{s.name}</span>
              <span style={{ color: 'var(--text-3)' }}>{s.used.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Over-limit warning */}
      {tier === 'over' && (
        <div style={{
          marginTop: 8, padding: '6px 10px',
          background: 'var(--danger-bg)',
          borderRadius: 'var(--r-sm)',
          fontSize: 11, color: 'var(--danger)',
          borderLeft: '2px solid var(--danger)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}>
          <span>{overLimitMessage}</span>
          {onUpgrade && (
            <button
              type="button"
              onClick={onUpgrade}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 11, fontWeight: 700,
                color: 'var(--danger)', textDecoration: 'underline',
              }}
            >
              Upgrade
            </button>
          )}
        </div>
      )}
    </div>
  );
}
